import React, { useEffect, useMemo, useRef, useState } from 'react';

import { METHOD_DEFINITIONS } from '../constants/methods';

import ContactIcon from '../Icons/Menu/ContactIcon';
import DocsIcon from '../Icons/Menu/DocsIcon';
import LogsIcon from '../Icons/Menu/LogsIcon';
import ImprintIcon from '../Icons/Menu/ImprintIcon';
import PrivacyIcon from '../Icons/Menu/PrivacyIcon';
import BookIcon from '../Icons/Menu/BookIcon';
import { LogoIcon } from '../Icon';

export default function MainLayout({ children, activeTab, setActiveTab }) {
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false);
  const [isStartMenuOpen, setIsStartMenuOpen] = useState(false);
  const startMenuRef = useRef(null);
  const menuDropdownRef = useRef(null);

  const HEADER_LINKS = [
    { id: 'recentLogs', label: 'Recent Logs', action: () => setActiveTab('recentLogs') },
    { id: 'blog', label: 'Blog', action: () => setActiveTab('blog') },
    { id: 'literature', label: 'Literature', action: () => setActiveTab('literature') },
    { id: 'privacy', label: 'Privacy Policy', action: () => setActiveTab('privacy') },
    { id: 'imprint', label: 'Imprint', action: () => setActiveTab('imprint') },
    { id: 'contact', label: 'Contact', action: () => setActiveTab('contact') },
  ];

  const isLanding = activeTab === 'landing';

  const STARTER_METHODS = useMemo(() => METHOD_DEFINITIONS, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!startMenuRef.current) {
        return;
      }

      if (!startMenuRef.current.contains(event.target)) {
        setIsStartMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsStartMenuOpen(false);
        setIsMenuDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    setIsStartMenuOpen(false);
    setIsMenuDropdownOpen(false);
  }, [activeTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target)) {
        setIsMenuDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const MENU_ITEMS = [
    { id: 'recentLogs', label: 'Recent Logs', action: () => setActiveTab('recentLogs'), icon: <LogsIcon size={20} /> },
    { id: 'contact', label: 'Contact', action: () => setActiveTab('contact'), icon: <ContactIcon size={20} /> },
    { id: 'privacy', label: 'Privacy Policy', action: () => setActiveTab('privacy'), icon: <PrivacyIcon size={20} /> },
    { id: 'imprint', label: 'Imprint', action: () => setActiveTab('imprint'), icon: <ImprintIcon size={20} /> },
    { id: 'blog', label: 'Blog', action: () => setActiveTab('blog'), icon: <DocsIcon size={20} /> },
    { id: 'literature', label: 'Literature', action: () => setActiveTab('literature'), icon: <BookIcon size={20} /> },
  ];

  const layoutClassName = `relative flex flex-col w-full text-white bg-gradient-to-br from-[#0f142b] via-[#1f1743] to-[#352a7a] ${isLanding ? 'min-h-[100dvh]' : 'min-h-screen'
    }`;

  return (
    <div className={layoutClassName}>
      <div className="pointer-events-none absolute inset-0 -z-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(192,132,252,0.26),rgba(8,11,28,0)_62%),radial-gradient(circle_at_bottom_right,rgba(124,58,237,0.28),rgba(8,11,28,0)_60%)]" />
        <div className="absolute inset-0 blur-[160px] bg-[radial-gradient(circle_at_top_left,rgba(192,132,252,0.45),transparent_55%),radial-gradient(circle_at_bottom_right,rgba(129,140,248,0.35),transparent_55%)] opacity-80" />
        <div className="absolute -top-44 -left-40 h-[32rem] w-[32rem] rounded-full bg-[radial-gradient(circle,rgba(236,72,153,0.55),rgba(79,70,229,0.2)_55%,transparent_75%)] blur-[120px] opacity-70" />
      </div>

      <div className="relative z-10 flex flex-1 flex-col">
        <header className="relative z-30 flex flex-wrap items-center gap-3 px-6 py-6 lg:flex-nowrap lg:items-center lg:justify-between lg:gap-6 lg:px-16 lg:py-8">
          <button
            type="button"
            onClick={() => setActiveTab('landing')}
            className="order-1 group flex items-center gap-3 text-left text-white transition hover:text-white/80"
          >
                       <LogoIcon
              size={null}
              className="h-10 w-10 transition group-hover:opacity-80 sm:h-12 sm:w-12"
            />

            <span className="text-3xl font-semibold tracking-wide sm:text-4xl translate-x-[-6px] translate-y-[6px] inline-block">
              umSolve
            </span>

          </button>

          <nav className="order-4 hidden w-full items-center justify-center gap-8 text-lg text-white/80 mt-5 lg:order-2 lg:flex lg:w-auto lg:flex-1 lg:justify-center lg:gap-10 lg:text-xl">
            {HEADER_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={link.action}
                className="transition hover:text-white"
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="order-2 ml-auto flex items-center gap-2 sm:gap-3 lg:order-3">
            <div ref={startMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setIsStartMenuOpen((prev) => !prev)}
                className={`mt-4 group relative flex items-center gap-1.5 overflow-hidden rounded-full border border-white/20 px-3 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.08em] 
                  text-white transition duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-300 sm:gap-2 sm:px-4 sm:text-[0.7rem] sm:tracking-[0.12em] 
                  md:text-xs lg:gap-2.5 lg:px-10 lg:py-4 lg:text-sm ${isStartMenuOpen
                    ? 'bg-white/15 shadow-[0_0_40px_rgba(236,72,153,0.5)]'
                    : 'bg-white/10 shadow-[0_0_30px_rgba(168,85,247,0.35)] hover:bg-white/15 hover:shadow-[0_0_40px_rgba(236,72,153,0.55)]'
                  }`}
                aria-haspopup="true"
                aria-expanded={isStartMenuOpen}
              >
                <span className="absolute inset-0 -z-10 bg-gradient-to-r from-[#8b5cf6]/80 via-[#a855f7]/80 to-[#ec4899]/80" />
                <span className="absolute inset-0 -z-20 blur-3xl bg-[radial-gradient(circle_at_left,rgba(139,92,246,0.65),transparent_55%),radial-gradient(circle_at_right,rgba(236,72,153,0.65),transparent_55%)] opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                <span className="relative z-10">Solve</span>
                <svg
                  className={`relative z-10 h-3 w-3 transition-transform duration-200 sm:h-3.5 sm:w-3.5 ${isStartMenuOpen ? 'rotate-180' : 'rotate-0'
                    }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {isStartMenuOpen && (
                <div className="absolute -right-6 top-full z-30 mt-3 w-80 overflow-hidden rounded-3xl border border-white/15 bg-[#0e1430]/95 p-3 shadow-[0_18px_55px_rgba(5,4,34,0.55)] backdrop-blur-xl sm:right-0">
                  <ul className="flex flex-col gap-2">
                    {STARTER_METHODS.map((method) => {
                      const icon = React.isValidElement(method.icon)
                        ? React.cloneElement(method.icon, { size: 30 })
                        : method.icon;

                      return (
                        <li key={method.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setActiveTab(method.id);
                              setIsStartMenuOpen(false);
                            }}
                            className="group relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-white/5 px-3 py-3 text-left transition duration-200 hover:-translate-y-[2px] hover:border-white/25 hover:bg-white/10"
                          >
                            <span
                              className="flex h-11 w-11 flex-none items-center justify-center rounded-full text-white shadow-[0_12px_25px_rgba(12,10,35,0.65)]"
                              style={{ backgroundColor: method.color }}
                            >
                              {icon}
                            </span>
                            <div className="flex min-w-0 flex-1 flex-col">
                              <span className="text-sm font-semibold text-white">{method.name}</span>
                              <span className="text-xs leading-snug text-white/60">
                                {method.description}
                              </span>
                            </div>
                            <svg
                              className="h-4 w-4 flex-none text-white/40 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-white/70"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-3 flex items-center justify-between rounded-2xl border border-dashed border-white/15 bg-white/5 px-3 py-3 text-sm text-white/70">
                    <div className="ml-20 flex flex-col">
                      <span className="font-semibold text-white">View more</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('methodPicker');
                        setIsStartMenuOpen(false);
                      }}
                      className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-[#8b5cf6] via-[#a855f7] to-[#ec4899] text-white shadow-[0_10px_30px_rgba(129,71,212,0.45)] transition hover:shadow-[0_12px_35px_rgba(236,72,153,0.55)]"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div ref={menuDropdownRef} className="relative lg:hidden">
              <button
                aria-label="Open navigation menu"
                onClick={() => setIsMenuDropdownOpen((prev) => !prev)}
                className="mt-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:h-11 sm:w-11"
              >
                <span className="flex h-5 flex-col items-center justify-between py-1">
                  <span className="block h-[2px] w-4 rounded-full bg-white sm:w-5" />
                  <span className="block h-[2px] w-4 rounded-full bg-white sm:w-5" />
                  <span className="block h-[2px] w-4 rounded-full bg-white sm:w-5" />
                </span>
              </button>

              {isMenuDropdownOpen && (
                <div className="absolute right-0 top-full z-30 mt-3 w-48 rounded-3xl border border-white/15 bg-[#0e1430]/95 p-2 shadow-[0_18px_55px_rgba(5,4,34,0.55)] backdrop-blur-xl">
                  <ul className="flex flex-col gap-1">
                    {MENU_ITEMS.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => {
                            item.action();
                            setIsMenuDropdownOpen(false);
                          }}
                          className="group flex w-full items-center gap-3 rounded-2xl border border-white/5 px-3 py-2 text-left text-sm text-white/80 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
                        >
                          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white">
                            {item.icon}
                          </span>
                          <span className="flex-1">{item.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </header>

        <main
          className={`relative z-10 flex flex-1 ${isLanding
              ? 'items-stretch px-0 -mb-[16px]'
              : 'items-start justify-center px-6 pb-16 lg:px-16'
            }`}
        >
          <div className={isLanding ? 'flex flex-1 min-h-0' : 'w-full max-w-[1600px]'}>{children}</div>
        </main>

        <footer className="px-6 text-center text-xs text-white/60 lg:px-16">
          <span className="relative inline-block -translate-y-8">
            © {new Date().getFullYear()} NumSolve. Built for curious problem solvers.
          </span>
        </footer>

      </div>
    </div>
  );
}
